import type { ErrorCodeEnum } from '.spec/self-care/tsTypes';
import { FORM_ERROR } from 'final-form';
import set from 'lodash/fp/set';
import type { ReactElement, ReactNode } from 'react';

import {
	type IntlShape,
	type MessageDescriptor,
	type PrimitiveType,
	defineMessages,
} from '@skippay/core/i18n/intl';

type BackendErrorCode = ErrorCodeEnum | string;

export type BackendError = {
	attribute?: string | null;
	code?: BackendErrorCode | null;
	context?: Record<string, unknown> | null;
	message?: string | null;
};

type BackendErrorsContainer =
	| BackendError[]
	| {
			data?: {
				errors?: BackendError[] | BackendError | null;
				messages?: {
					errors?: BackendError[] | BackendError | null;
				} | null;
			} | null;
			errors?: BackendError[] | BackendError | null;
	  }
	| null
	| undefined;

type IntlFormatter = Pick<IntlShape, 'formatMessage'>;

type MessageDescriptorFactory = (error: BackendError) => MessageDescriptor | undefined;
type IntlMessageValues = Record<string, PrimitiveType>;
type BackendValidationErrors = Record<string, string> &
	Partial<Record<typeof FORM_ERROR, string[]>>;

export type RichMessageValues = Record<
	string,
	ReactElement | ((chunks: ReactNode) => ReactElement)
>;

const {
	GENERIC_ERROR_MESSAGE,
	ACCOUNT_ALREADY_EXISTS_MESSAGE,
	EMAIL_ALREADY_EXISTS_MESSAGE,
	INVALID_VALUE_MESSAGE,
	MAXIMUM_VALUE_MESSAGE,
	REQUIRED_VALUE_MESSAGE,
	INVALID_OTP_CODE_MESSAGE,
	UNSUPPORTED_REGISTRATION_COUNTRY_MESSAGE,
	ACTIVITY_ALREADY_EXISTS_MESSAGE,
} = defineMessages({
	GENERIC_ERROR_MESSAGE: {
		defaultMessage: 'Něco se pokazilo. Zkuste to prosím znovu.',
		id: 'backendErrors.generic',
	},
	ACCOUNT_ALREADY_EXISTS_MESSAGE: {
		defaultMessage: 'Vypadá to, že už se známe.',
		id: 'AlreadyHasAnAccount.title',
	},
	EMAIL_ALREADY_EXISTS_MESSAGE: {
		defaultMessage: 'Registrace s touto e-mailovou adresou už existuje. Zvolte prosím jinou.',
		id: 'ChangeAccountEmailModal.error.existingEmail',
	},
	INVALID_VALUE_MESSAGE: {
		defaultMessage: 'Neplatná hodnota.',
		id: 'validationErrors.invalid',
	},
	MAXIMUM_VALUE_MESSAGE: {
		defaultMessage: 'Překročeno maximum.',
		id: 'validationErrors.maximum',
	},
	REQUIRED_VALUE_MESSAGE: {
		defaultMessage: 'Toto pole je povinné.',
		id: 'validationErrors.required',
	},
	INVALID_OTP_CODE_MESSAGE: {
		defaultMessage: 'Neplatný kód, nechte si zaslat nový.',
		id: 'validationErrors.invalidCodeResendNew',
	},
	UNSUPPORTED_REGISTRATION_COUNTRY_MESSAGE: {
		defaultMessage:
			'Mrzí nás to, vzhledem ke složitějším právním procesům a zákonům zatím neumíme zpracovat tuto žádost.',
		id: 'backendErrors.UNSUPPORTED_REGISTRATION_COUNTRY',
	},
	ACTIVITY_ALREADY_EXISTS_MESSAGE: {
		defaultMessage:
			'Požadavek na aktivitu {activityName} pro telefonní číslo {phoneNumber} už existuje.',
		id: 'backendErrors.ALREADY_EXISTS',
	},
});

const STATIC_MESSAGE_DESCRIPTORS: Record<string, MessageDescriptor> = defineMessages({
	ADDITIONAL_DATA_NOT_MATCHING: {
		defaultMessage: 'Doplňující údaje se neshodují.',
		id: 'backendErrors.ADDITIONAL_DATA_NOT_MATCHING',
	},
	ADDITIONAL_DATA_NOT_REQUIRED: {
		defaultMessage: 'Doplňující údaje nejsou vyžadovány.',
		id: 'backendErrors.ADDITIONAL_DATA_NOT_REQUIRED',
	},
	ALREADY_CONNECTED: {
		defaultMessage: 'Účet je už propojený. Přihlaste se prosím.',
		id: 'backendErrors.ALREADY_CONNECTED',
	},
	ALREADY_CONNECTED_NOT_FINISHED: {
		defaultMessage: 'Účet je už propojený. Dokončete prosím registraci.',
		id: 'backendErrors.ALREADY_CONNECTED_NOT_FINISHED',
	},
	APPLICATION_ALREADY_CANCELLED: {
		defaultMessage: 'Žádost už byla zrušena.',
		id: 'backendErrors.APPLICATION_ALREADY_CANCELLED',
	},
	APPLICATION_PAID: {
		defaultMessage: 'Žádost už byla zaplacena.',
		id: 'backendErrors.APPLICATION_PAID',
	},
	APPLICATION_REJECTED: {
		defaultMessage: 'Žádost byla zamítnuta.',
		id: 'backendErrors.APPLICATION_REJECTED',
	},
	AUTHORIZATION_EXPIRED: {
		defaultMessage: 'Platnost kódu vypršela. Požádejte o nový kód a zkuste to znovu.',
		id: 'ChangePhoneEnterOtpScreen.error.authorizationExpired',
	},
	AUTHORIZATION_REQUEST_EXPIRED: {
		defaultMessage: 'Zaslaný kód byl zneplatněn. Abyste mohli pokračovat, nechte si zaslat nový.',
		id: 'backendErrors.AUTHORIZATION_REQUEST_EXPIRED',
	},
	CANCELLATION_NOT_SUPPORTED: {
		defaultMessage: 'Žádost v tomto stavu nemůže být zrušena.',
		id: 'backendErrors.CANCELLATION_NOT_SUPPORTED',
	},
	CANCELLATION_TOTAL_PRICE_TOO_HIGH: {
		defaultMessage: 'Částka musí být nižší než celková cena objednávky.',
		id: 'backendErrors.CANCELLATION_TOTAL_PRICE_TOO_HIGH',
	},
	CARD_ACTIVATION_INVALID_STATE: {
		defaultMessage: 'Platební kartu nelze aktivovat z aktuálního stavu.',
		id: 'backendErrors.CARD_ACTIVATION_INVALID_STATE',
	},
	CARD_ACTIVATION_NOT_ALLOWED_SECURITY: {
		defaultMessage: 'Kartu nelze odblokovat z bezpečnostních důvodů. Kontaktujte podporu.',
		id: 'backendErrors.CARD_ACTIVATION_NOT_ALLOWED_SECURITY',
	},
	CARD_LAYOUT_RETRIEVAL_INVALID_STATE: {
		defaultMessage: 'Platební karta není ve stavu umožňujícím získání vzhledu karty.',
		id: 'backendErrors.CARD_LAYOUT_RETRIEVAL_INVALID_STATE',
	},
	CARD_PIN_RETRIEVAL_INVALID_STATE: {
		defaultMessage: 'Platební karta není ve stavu umožňujícím získání PINu.',
		id: 'backendErrors.CARD_PIN_RETRIEVAL_INVALID_STATE',
	},
	CONNECTION_ERROR: {
		defaultMessage: 'Nepodařilo se dokončit přihlášení. Zkuste to prosím znovu.',
		id: 'backendErrors.CONNECTION_ERROR',
	},
	CURRENT_PASSWORD_WRONG: {
		defaultMessage: 'Současné heslo není správné.',
		id: 'backendErrors.CURRENT_PASSWORD_WRONG',
	},
	CUSTOMER_ACCESS_NOT_ENABLED: {
		defaultMessage: 'Zákazník nemá povolený přístup.',
		id: 'backendErrors.CUSTOMER_ACCESS_NOT_ENABLED',
	},
	DEFERMENT_NOT_ALLOWED: {
		defaultMessage: 'Odklad vyúčtování není povolen.',
		id: 'backendErrors.DEFERMENT_NOT_ALLOWED',
	},
	DEFERMENT_NOT_ALLOWED_ALREADY_DEFERRED: {
		defaultMessage: 'Vyúčtování může být odloženo pouze jedenkrát.',
		id: 'backendErrors.DEFERMENT_NOT_ALLOWED_ALREADY_DEFERRED',
	},
	DEFERMENT_NOT_ALLOWED_BALANCE_TOO_LOW: {
		defaultMessage: 'Saldo vyúčtování je příliš nízké, vyúčtování nemůže být odloženo.',
		id: 'backendErrors.DEFERMENT_NOT_ALLOWED_BALANCE_TOO_LOW',
	},
	DEFERMENT_NOT_ALLOWED_BILLING_UNPAYABLE: {
		defaultMessage: 'Odklad vyúčtování není povolen, vyúčtování nelze zaplatit.',
		id: 'backendErrors.DEFERMENT_NOT_ALLOWED_BILLING_UNPAYABLE',
	},
	DEFERMENT_NOT_ALLOWED_LOAN_ACCELERATED: {
		defaultMessage: 'Odklad vyúčtování není povolen, úvěrová smlouva byla zesplatněna.',
		id: 'backendErrors.DEFERMENT_NOT_ALLOWED_LOAN_ACCELERATED',
	},
	DEFERMENT_NOT_ALLOWED_ZERO_AMOUNT: {
		defaultMessage: 'Odklad vyúčtování není povolen, nulovou částku nelze odložit.',
		id: 'backendErrors.DEFERMENT_NOT_ALLOWED_ZERO_AMOUNT',
	},
	EMAIL_CHANGE_EMAIL_UNAVAILABLE: {
		defaultMessage: 'Zvolený e-mail nelze použít.',
		id: 'backendErrors.EMAIL_CHANGE_EMAIL_UNAVAILABLE',
	},
	EMAIL_CHANGE_LINK_EXPIRED: {
		defaultMessage: 'Ups, odkaz už není platný. Požádejte o novou změnu e-mailu.',
		id: 'backendErrors.EMAIL_CHANGE_LINK_EXPIRED',
	},
	EMAIL_CHANGE_NOT_ALLOWED: {
		defaultMessage: 'E-mail není možné změnit.',
		id: 'backendErrors.EMAIL_CHANGE_NOT_ALLOWED',
	},
	EMAIL_CHANGE_FAILURE_PHONE_RECENTLY_CHANGED: {
		defaultMessage:
			'Z bezpečnostních důvodů není momentálně změna e-mailu k dispozici. Potřebujete-li přesto e-mail změnit, <link_contact>kontaktujte nás</link_contact>.',
		id: 'backendErrors.EMAIL_CHANGE_FAILURE_PHONE_RECENTLY_CHANGED',
	},
	EMAILS_DIFFER: {
		defaultMessage: 'E-mailová adresa se neshoduje s existujícím účtem.',
		id: 'backendErrors.EMAILS_DIFFER',
	},
	ESHOP_DOES_NOT_EXIST: {
		defaultMessage: 'E-shop neexistuje.',
		id: 'backendErrors.ESHOP_DOES_NOT_EXIST',
	},
	EXPLOITED_MAX_SMS_SENT: {
		defaultMessage: 'Dosáhli jste maximálního počtu odeslaných SMS. Zkuste to prosím později.',
		id: 'backendErrors.EXPLOITED_MAX_SMS_SENT',
	},
	MAX_REQUEST_ATTEMPTS_REACHED: {
		defaultMessage: 'Příliš mnoho pokusů o autorizaci. Zkuste to prosím znovu později.',
		id: 'backendErrors.MAX_REQUEST_ATTEMPTS_REACHED',
	},
	MAX_SMS_ATTEMPTS_REACHED: {
		defaultMessage:
			'Dosáhli jste maximálního počtu SMS pro autorizaci. Zkuste to prosím znovu později.',
		id: 'backendErrors.MAX_SMS_ATTEMPTS_REACHED',
	},
	FORBIDDEN: {
		defaultMessage: 'Tuto akci teď není možné provést.',
		id: 'backendErrors.FORBIDDEN',
	},
	INTERNAL_SERVER_ERROR: {
		defaultMessage: 'Na serveru došlo k chybě. Zkuste to prosím znovu.',
		id: 'backendErrors.INTERNAL_SERVER_ERROR',
	},
	INVALID_APPLICATION_STATE: {
		defaultMessage: 'Tuto akci teď není možné provést kvůli aktuálnímu stavu žádosti.',
		id: 'backendErrors.INVALID_APPLICATION_STATE',
	},
	INVALID_CAPTCHA: {
		defaultMessage: 'Ověření se nepodařilo. Zkuste to prosím znovu.',
		id: 'backendErrors.INVALID_CAPTCHA',
	},
	INVALID_COUNTRY: {
		defaultMessage: 'Zadaná země není podporovaná.',
		id: 'backendErrors.INVALID_COUNTRY',
	},
	INVALID_CURRENCY: {
		defaultMessage: 'Zadaná měna není podporovaná.',
		id: 'backendErrors.INVALID_CURRENCY',
	},
	INVALID_CURRENCY_ESHOP_MISMATCH: {
		defaultMessage: 'Měna žádosti musí odpovídat měně e-shopu.',
		id: 'backendErrors.INVALID_CURRENCY_ESHOP_MISMATCH',
	},
	INVALID_CURRENCY_MISMATCH: {
		defaultMessage: 'Měna neodpovídá měně původní žádosti.',
		id: 'backendErrors.INVALID_CURRENCY_MISMATCH',
	},
	INVALID_DEVICE_TYPE: {
		defaultMessage: 'Neplatný typ zařízení.',
		id: 'backendErrors.INVALID_DEVICE_TYPE',
	},
	INVALID_ESHOP: {
		defaultMessage: 'Neplatný e-shop.',
		id: 'backendErrors.INVALID_ESHOP',
	},
	INVALID_NAME: {
		defaultMessage: 'Zadejte platné křestní jméno a příjmení.',
		id: 'backendErrors.INVALID_NAME',
	},
	INVALID_NAME_FIELDS: {
		defaultMessage: 'Vyplňte křestní jméno nebo celé jméno.',
		id: 'backendErrors.INVALID_NAME_FIELDS',
	},
	INVALID_RECURRING_PAYMENT_STATE: {
		defaultMessage: 'Neplatný stav autorizace opakované platby.',
		id: 'backendErrors.INVALID_RECURRING_PAYMENT_STATE',
	},
	INVALID_LOGIN: {
		defaultMessage: 'Zadejte správnou kombinaci e-mailu a hesla.',
		id: 'backendErrors.INVALID_LOGIN',
	},
	INVALID_REQUEST: {
		defaultMessage: 'Požadavek není platný.',
		id: 'backendErrors.INVALID_REQUEST',
	},
	MERCHANT_ID_NOT_EXIST: {
		defaultMessage: 'Zadaný identifikátor obchodníka neexistuje.',
		id: 'backendErrors.MERCHANT_ID_NOT_EXIST',
	},
	MERCHANT_ID_REQUIRED: {
		defaultMessage: 'Chybí identifikátor obchodníka.',
		id: 'backendErrors.MERCHANT_ID_REQUIRED',
	},
	MULTIPLE_CURRENCIES: {
		defaultMessage: 'Není možné kombinovat více měn.',
		id: 'backendErrors.MULTIPLE_CURRENCIES',
	},
	NEW_PASSWORD_EQUALS_CURRENT_PASSWORD: {
		defaultMessage: 'Nové heslo nesmí být stejné jako to aktuální.',
		id: 'backendErrors.NEW_PASSWORD_EQUALS_CURRENT_PASSWORD',
	},
	NOT_ALLOWED: {
		defaultMessage: 'Tuto akci není možné provést.',
		id: 'backendErrors.NOT_ALLOWED',
	},
	NOT_AUTHORIZED: {
		defaultMessage: 'K této akci nemáte oprávnění.',
		id: 'backendErrors.NOT_AUTHORIZED',
	},
	NOT_AUTHORIZED_BLOCKED: {
		defaultMessage: 'Přístup je dočasně zablokován.',
		id: 'backendErrors.NOT_AUTHORIZED_BLOCKED',
	},
	NOT_CONNECTED: {
		defaultMessage: 'Připojení se nezdařilo.',
		id: 'backendErrors.NOT_CONNECTED',
	},
	NOT_SUPPORTED: {
		defaultMessage: 'Tato akce není podporovaná.',
		id: 'backendErrors.NOT_SUPPORTED',
	},
	NO_VERIF_MOBILE: {
		defaultMessage: 'Pro tento účet nemáme ověřené telefonní číslo.',
		id: 'backendErrors.NO_VERIF_MOBILE',
	},
	OBJECT_NOT_FOUND: {
		defaultMessage: 'Požadovaný objekt nebyl nalezen.',
		id: 'backendErrors.OBJECT_NOT_FOUND',
	},
	OTP_INCORRECT: {
		defaultMessage:
			'Neplatný kód. Zkuste to znovu, zbývá vám {attemptsRemaining, plural, one {# pokus} few {# pokusy} other {# pokusů}}.',
		id: 'backendErrors.OTP_INCORRECT',
	},
	OTP_MAX_ATTEMPTS_REACHED: {
		defaultMessage: 'Zaslaný kód byl zneplatněn. Abyste mohli pokračovat, nechte si zaslat nový.',
		id: 'backendErrors.OTP_MAX_ATTEMPTS_REACHED',
	},
	PAGINATION: {
		defaultMessage: 'Nepodařilo se načíst další data.',
		id: 'backendErrors.PAGINATION',
	},
	PAGINATION_CONFLICTING_CURSORS: {
		defaultMessage: 'Hodnoty stránkování před a po nemohou být nastaveny najednou.',
		id: 'backendErrors.PAGINATION_CONFLICTING_CURSORS',
	},
	PAGINATION_SIZE_NOT_INTEGER: {
		defaultMessage: 'Hodnota velikosti stránky musí být celé číslo.',
		id: 'backendErrors.PAGINATION_SIZE_NOT_INTEGER',
	},
	PAGINATION_SIZE_TOO_LARGE: {
		defaultMessage: 'Hodnota velikosti stránky musí být menší než povolené maximum.',
		id: 'backendErrors.PAGINATION_SIZE_TOO_LARGE',
	},
	PAGINATION_SIZE_TOO_SMALL: {
		defaultMessage: 'Hodnota velikosti stránky musí být větší než nula.',
		id: 'backendErrors.PAGINATION_SIZE_TOO_SMALL',
	},
	PAYMENT_ORDER_LIMIT_PER_HOUR_EXCEEDED: {
		defaultMessage:
			'Bohužel jste zadali v krátké době příliš mnoho plateb. Zkuste to prosím později.',
		id: 'backendErrors.PAYMENT_ORDER_LIMIT_PER_HOUR_EXCEEDED',
	},
	PAYMENT_ORDER_TEMPLATE_LIMIT_EXCEEDED: {
		defaultMessage: 'Bohužel jste dosáhli maximálního počtu pravidelných plateb.',
		id: 'backendErrors.PAYMENT_ORDER_TEMPLATE_LIMIT_EXCEEDED',
	},
	PERSONAL_ID_INVALID: {
		defaultMessage: 'Rodné číslo není platné.',
		id: 'backendErrors.PERSONAL_ID_INVALID',
	},
	PHONE_CHANGE_FAILURE: {
		defaultMessage: 'Zadaný kód již není platný. Zkuste to prosím znovu.',
		id: 'ChangePhoneEnterOtpScreen.error.invalidOtp',
	},
	PHONE_CHANGE_FAILURE_ALREADY_EXISTS: {
		defaultMessage:
			'Toto telefonní číslo nelze použít. Zvolte prosím jiné nebo <link_contact>nás kontaktujte</link_contact>.',
		id: 'backendErrors.PHONE_CHANGE_FAILURE_ALREADY_EXISTS',
	},
	PHONE_CHANGE_FAILURE_NOT_POSSIBLE_YET: {
		defaultMessage:
			'Telefonní číslo můžete z bezpečnostních důvodů změnit jen jednou za půl roku. Pokud potřebujete číslo aktualizovat dříve, <link_contact>kontaktujte nás</link_contact>.',
		id: 'backendErrors.PHONE_CHANGE_FAILURE_NOT_POSSIBLE_YET',
	},
	PHONE_CHANGE_FAILURE_TOO_MANY_ATTEMPTS: {
		defaultMessage:
			'Z bezpečnostních důvodů není momentálně změna čísla k dispozici. Potřebujete-li přesto telefon změnit, <link_contact>kontaktujte nás</link_contact>.',
		id: 'backendErrors.PHONE_CHANGE_FAILURE_TOO_MANY_ATTEMPTS',
	},
	PHONE_CHANGE_FAILURE_EMAIL_RECENTLY_CHANGED: {
		defaultMessage:
			'Z bezpečnostních důvodů není momentálně změna čísla k dispozici. Potřebujete-li přesto telefon změnit,  <link_contact>kontaktujte nás</link_contact>.',
		id: 'backendErrors.PHONE_CHANGE_FAILURE_EMAIL_RECENTLY_CHANGED',
	},
	PUSH_NOTIFICATION_DEVICE_EXISTS: {
		defaultMessage: 'Toto zařízení je už pro push notifikace registrované.',
		id: 'backendErrors.PUSH_NOTIFICATION_DEVICE_EXISTS',
	},
	RECURRING_PAYMENT_NOT_ALLOWED: {
		defaultMessage: 'Opakovaná platba není povolena.',
		id: 'backendErrors.RECURRING_PAYMENT_NOT_ALLOWED',
	},
	REGISTRATION_NOT_SUPPORTED: {
		defaultMessage: 'Registrace není pro tuto zemi povolena.',
		id: 'backendErrors.REGISTRATION_NOT_SUPPORTED',
	},
	REPEATED_REQUEST_TOO_EARLY: {
		defaultMessage: 'Požadavek byl odeslán příliš brzy. Zkuste to prosím znovu později.',
		id: 'backendErrors.REPEATED_REQUEST_TOO_EARLY',
	},
	TEMPORARY_OUTAGE: {
		defaultMessage: 'Služba je dočasně nedostupná. Zkuste to prosím později.',
		id: 'backendErrors.TEMPORARY_OUTAGE',
	},
	TOKEN_FOR_SAME_UUID_EXISTS: {
		defaultMessage: 'Pro toto zařízení už existuje aktivní token.',
		id: 'backendErrors.TOKEN_FOR_SAME_UUID_EXISTS',
	},
	TOTAL_PRICE_LIMIT_EXCEEDED: {
		defaultMessage: 'Celková cena překračuje povolený limit.',
		id: 'backendErrors.TOTAL_PRICE_LIMIT_EXCEEDED',
	},
	UNAUTHORIZED: {
		defaultMessage: 'Neoprávněný přístup.',
		id: 'backendErrors.UNAUTHORIZED',
	},
	UNAUTHORIZED_AND_TOKEN_INVALID: {
		defaultMessage: 'Přihlášení vypršelo nebo je token neplatný.',
		id: 'backendErrors.UNAUTHORIZED_AND_TOKEN_INVALID',
	},
	USER_PHONE_NOT_PROVIDED: {
		defaultMessage: 'Telefon uživatele není k dispozici.',
		id: 'backendErrors.USER_PHONE_NOT_PROVIDED',
	},
	UUID_NOT_VALID: {
		defaultMessage: 'Identifikátor není platný.',
		id: 'backendErrors.UUID_NOT_VALID',
	},
});

const SHARED_MESSAGE_DESCRIPTORS: Record<string, MessageDescriptor> = {
	CZ_INVALID_COUNTRY_OF_REGISTRATION: UNSUPPORTED_REGISTRATION_COUNTRY_MESSAGE,
	CZ_USER_EXISTS: ACCOUNT_ALREADY_EXISTS_MESSAGE,
	EMAIL_CHANGE_FAILURE: EMAIL_ALREADY_EXISTS_MESSAGE,
	HU_INVALID_COUNTRY_OF_REGISTRATION: UNSUPPORTED_REGISTRATION_COUNTRY_MESSAGE,
	HU_USER_EXISTS: ACCOUNT_ALREADY_EXISTS_MESSAGE,
	INVALID: INVALID_VALUE_MESSAGE,
	INVALID_OTP_CODE: INVALID_OTP_CODE_MESSAGE,
	MAX_LENGTH: MAXIMUM_VALUE_MESSAGE,
	REQUIRED: REQUIRED_VALUE_MESSAGE,
	SK_INVALID_COUNTRY_OF_REGISTRATION: UNSUPPORTED_REGISTRATION_COUNTRY_MESSAGE,
	SK_USER_EXISTS: ACCOUNT_ALREADY_EXISTS_MESSAGE,
};

const MESSAGE_DESCRIPTORS: Partial<
	Record<ErrorCodeEnum, MessageDescriptor | MessageDescriptorFactory>
> = {
	ALREADY_EXISTS: (error) =>
		error.context?.activityName && error.context?.phoneNumber
			? ACTIVITY_ALREADY_EXISTS_MESSAGE
			: error.message
				? undefined
				: ACCOUNT_ALREADY_EXISTS_MESSAGE,
	...SHARED_MESSAGE_DESCRIPTORS,
	...STATIC_MESSAGE_DESCRIPTORS,
};

export const isAuthorizationExpiredCode = (code?: BackendErrorCode | null): boolean =>
	code === 'AUTHORIZATION_EXPIRED' || code === 'AUTHORIZATION_REQUEST_EXPIRED';

export const isEmailChangeLinkExpiredCode = (code?: BackendErrorCode | null): boolean =>
	code === 'AUTHORIZATION_EXPIRED' || code === 'EMAIL_CHANGE_LINK_EXPIRED';

const isBackendError = (value: unknown): value is BackendError =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toBackendErrorArray = (
	value?: BackendError[] | BackendError | null
): BackendError[] | undefined => {
	if (!value) {
		return undefined;
	}

	if (Array.isArray(value)) {
		return value.filter(isBackendError);
	}

	return isBackendError(value) ? [value] : undefined;
};

const getDescriptor = (error: BackendError): MessageDescriptor | undefined => {
	if (!error.code) {
		return undefined;
	}

	const descriptor = MESSAGE_DESCRIPTORS[error.code];
	if (!descriptor) {
		return undefined;
	}

	return typeof descriptor === 'function' ? descriptor(error) : descriptor;
};

const toIntlMessageValues = (
	context?: Record<string, unknown> | null
): IntlMessageValues | undefined => {
	if (!context) {
		return undefined;
	}

	const values = Object.entries(context).reduce<IntlMessageValues>((acc, [key, value]) => {
		if (
			typeof value === 'string' ||
			typeof value === 'number' ||
			typeof value === 'boolean' ||
			value instanceof Date
		) {
			acc[key] = value;
		}

		return acc;
	}, {});

	return Object.keys(values).length ? values : undefined;
};

export const formatGenericBackendError = (intl?: IntlFormatter): string =>
	intl ? intl.formatMessage(GENERIC_ERROR_MESSAGE) : GENERIC_ERROR_MESSAGE.defaultMessage;

export const normalizeBackendErrors = (input?: BackendErrorsContainer): BackendError[] => {
	if (Array.isArray(input)) {
		return input.filter(isBackendError);
	}

	if (!input || typeof input !== 'object') {
		return [];
	}

	return (
		toBackendErrorArray(input.data?.errors) ||
		toBackendErrorArray(input.data?.messages?.errors) ||
		toBackendErrorArray(input.errors) ||
		[]
	);
};

export function formatBackendError(
	error?: BackendError | null,
	richValues?: RichMessageValues,
	intl?: IntlFormatter
): string | ReactNode {
	if (!error) {
		return formatGenericBackendError(intl);
	}

	const descriptor = getDescriptor(error);
	if (descriptor && intl) {
		try {
			return intl.formatMessage(descriptor, {
				...toIntlMessageValues(error.context),
				...richValues,
			});
		} catch {
			// Invalid or missing context should fall back to the backend message.
		}
	}

	if (error.message) {
		return error.message;
	}

	return formatGenericBackendError(intl);
}

export const toBackendValidationErrors = (
	input: BackendErrorsContainer,
	formatError: (error?: BackendError | null) => string | ReactNode = formatBackendError
): BackendValidationErrors | undefined => {
	const errors = normalizeBackendErrors(input);

	if (!errors.length) {
		return undefined;
	}

	return errors.reduce<BackendValidationErrors>((acc, error) => {
		const formattedError = formatError(error) as string;

		if (error.attribute) {
			return set(error.attribute, formattedError, acc);
		}

		acc[FORM_ERROR] = (acc[FORM_ERROR] || []).concat(formattedError);
		return acc;
	}, {});
};
